/**
 * Cost Charts Components
 * Pie chart for cost breakdown and line chart for daily trends
 */

import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  AreaChart,
} from 'recharts';
import type { CostSummary, DailyStats } from '@/lib/api/reports';
import { getCostMixItems } from '../pricingTerms';

const COLORS = {
  chat: '#ef4444',
  dailyPush: '#f59e0b',
  charm: '#3b82f6',
  soulmate: '#8b5cf6',
};

interface CostPieChartProps {
  summary: CostSummary;
  className?: string;
}

export function CostPieChart({ summary, className }: CostPieChartProps) {
  const data = useMemo(() => {
    const toneToColor: Record<string, string> = {
      neutral: COLORS.chat,
      warning: COLORS.dailyPush,
      info: COLORS.soulmate,
      danger: '#10b981',
      profit: '#3b82f6',
    };
    return getCostMixItems(summary).map((item) => ({
      name: item.label,
      value: item.value,
      color: toneToColor[item.tone] ?? COLORS.charm,
    }));
  }, [summary]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">成本构成饼图</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return `¥${numValue.toFixed(2)}`;
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface DailyTrendChartProps {
  dailyStats: DailyStats[];
  className?: string;
}

export function DailyTrendChart({ dailyStats, className }: DailyTrendChartProps) {
  const data = useMemo(() => {
    return [...dailyStats]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        date: d.date.slice(5), // MM-DD format
        cost: d.chat_cost_cny,
        sessions: d.sessions,
        users: d.users,
      }));
  }, [dailyStats]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">每日聊天成本趋势(LLM)</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#888" fontSize={12} />
            <YAxis
              stroke="#888"
              fontSize={12}
              tickFormatter={(v) => `¥${v.toFixed(0)}`}
            />
            <Tooltip
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return [`¥${numValue.toFixed(2)}`, '聊天成本'];
              }}
              labelFormatter={(label) => `日期: ${label}`}
            />
            <Area
              type="monotone"
              dataKey="cost"
              stroke="#ef4444"
              fill="rgba(239, 68, 68, 0.1)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface SessionsTrendChartProps {
  dailyStats: DailyStats[];
  className?: string;
}

export function SessionsTrendChart({ dailyStats, className }: SessionsTrendChartProps) {
  const data = useMemo(() => {
    return [...dailyStats]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        date: d.date.slice(5),
        sessions: d.sessions,
        users: d.users,
      }));
  }, [dailyStats]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">每日会话数</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#888" fontSize={12} />
            <YAxis stroke="#888" fontSize={12} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="sessions"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
              name="会话数"
            />
            <Line
              type="monotone"
              dataKey="users"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
              name="用户数"
            />
            <Legend />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
